// src/pages/LeaveReview.tsx (or wherever you keep pages)

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/ui/navbar";
import { Button } from "@/components/ui/button";

export default function LeaveReview() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: (formData.get("name") as string) || "",
      country: (formData.get("country") as string) || "",
      rating: (formData.get("rating") as string) || "",
      review: (formData.get("review") as string) || "",
      allow_display: formData.get("allow_display") === "on",
    };

    try {
      const res = await fetch("/api/send-review-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to send review");
      }

      setSubmitted(true);
      form.reset();
    } catch (error) {
      console.error(error);
      alert("There was an issue sending your review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Thank-you state
  if (submitted) {
    return (
      <>
        <Navbar />
        <div className="max-w-lg mx-auto pt-[80px] px-4 pb-16 text-center">
          <h1 className="text-2xl font-semibold text-[#1C1F26]">
            Thank you for your review 🎉
          </h1>
          <p className="text-gray-600 mt-3">
            Your feedback was sent to the Prep2Pass team. We’ll review it before
            publishing anything on the website.
          </p>

          <Button
            className="mt-6"
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
        </div>
      </>
    );
  }

  // ✅ Form state
  return (
    <>
      <Navbar />

      <div className="max-w-lg mx-auto pt-[80px] px-4 pb-16">
        <h1 className="text-2xl font-semibold text-[#1C1F26] mb-2">
          Leave a review
        </h1>
        <p className="text-gray-600 mb-6 text-sm">
          This goes directly to our team. We’ll only publish your review on the website
          if you give us permission.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name (optional)
            </label>
            <input
              id="name"
              name="name"
              className="w-full border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="First name or initials"
            />
          </div>

          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Country (optional)
            </label>
            <input
              id="country"
              name="country"
              className="w-full border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Where are you preparing from?"
            />
          </div>

          <div>
            <label
              htmlFor="rating"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Star rating
            </label>
            <select
              id="rating"
              name="rating"
              required
              className="w-full border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a rating</option>
              <option value="1">⭐ 1 – Not great</option>
              <option value="2">⭐⭐ 2 – Needs improvement</option>
              <option value="3">⭐⭐⭐ 3 – Okay</option>
              <option value="4">⭐⭐⭐⭐ 4 – Very helpful</option>
              <option value="5">⭐⭐⭐⭐⭐ 5 – Amazing</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="review"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Your review
            </label>
            <textarea
              id="review"
              name="review"
              required
              className="w-full border rounded-lg p-3 text-sm h-32 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Tell us how Prep2Pass helped with your TCF Canada prep..."
            />
          </div>

          <label className="flex gap-2 text-sm text-gray-600 items-start">
            <input
              type="checkbox"
              name="allow_display"
              required
              className="mt-[3px]"
            />
            <span>
              I allow Prep2Pass to publish this review on the website. My email will
              never be shown publicly.
            </span>
          </label>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-3 font-semibold"
          >
            {loading ? "Sending..." : "Submit review"}
          </Button>
        </form>
      </div>
    </>
  );
}
